import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { Footer } from '../components/Footer';
export const ThankYouPage = () => {
  const steps = [{
    number: 1,
    title: 'Confirm Your Call',
    content: <>
          <p className="mb-4" data-id="element-521">
            Confirm your call by replying "Yes" to our text or email and
            selecting "Yes" in the email calendar invitation.
          </p>
          <p data-id="element-522">
            Search your inbox for "Equip Sales Academy" or "Sales OS Audit".
            Click "I know the sender" and "Yes" for the calendar invitation.
          </p>
        </>,
    hasImage: true
  }, {
    number: 2,
    title: 'Prepare your questions in advance.',
    content: <p data-id="element-523">
          On the call, we'll be getting to know you, your business, and your
          goals. We want the call to be as helpful to you as possible (even if
          we don't work together), so please have all your questions prepared
          for us in advance so you can leave with clarity and confidence.
        </p>,
    hasImage: false
  }, {
    number: 3,
    title: 'Block out the time so there are no distractions.',
    content: <p data-id="element-524">
          For this to be a productive call, please give us your full attention
          during the time. If you have other commitments during your booked
          time, feel free to reach out to us and reschedule the call.
        </p>,
    hasImage: false
  }];
  return <div className="w-full min-h-screen bg-[#020311]" data-id="element-525">
      {/* Header with Logo */}
      <div className="flex justify-center py-8 sm:py-12" data-id="element-526">
        <a href="/" data-id="element-527">
          <img src="https://cdn.magicpatterns.com/uploads/pPv8sZAUYxABkDBucNAnxU/esa.png" alt="Equip Sales Academy Logo" className="h-14 sm:h-16 md:h-20" data-id="element-528" />
        </a>
      </div>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 pb-16" data-id="element-529">
        {/* Success Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12 sm:mb-16" data-id="element-530">
          {/* Checkmark Icon with Wipe Circle Animation */}
          <div className="flex justify-center mb-6" data-id="element-531">
            <motion.div initial={{
            clipPath: 'circle(0% at 50% 50%)'
          }} animate={{
            clipPath: 'circle(50% at 50% 50%)'
          }} transition={{
            duration: 0.8,
            delay: 0.3,
            ease: [0.34, 1.56, 0.64, 1]
          }} className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-[#6366F1] flex items-center justify-center" data-id="element-532">
              <motion.div initial={{
              scale: 0,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 0.4,
              delay: 0.7,
              ease: 'easeOut'
            }} data-id="element-533">
                <CheckCircle2 className="w-12 h-12 sm:w-14 sm:h-14 text-[#6366F1]" data-id="element-534" />
              </motion.div>
            </motion.div>
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-[#F5F5F7] leading-tight" data-id="element-535">
            Your Sales OS Audit Call
            <br data-id="element-536" />
            Has Been Booked!
          </h1>
        </motion.div>

        {/* Steps */}
        <div className="space-y-8 sm:space-y-10" data-id="element-537">
          {steps.map((step, index) => <motion.div key={step.number} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2 + index * 0.15
        }} className="bg-[#0A0A14] border border-[#6366F1]/20 rounded-xl p-6 sm:p-8" data-id="element-538">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#6366F1] mb-4 italic" data-id="element-539">
                Step {step.number}: {step.title}
              </h2>
              <div className="text-[#F5F5F7]/80 text-base sm:text-lg leading-relaxed italic" data-id="element-540">
                {step.content}
              </div>

              {step.hasImage && <div className="mt-6 rounded-lg overflow-hidden border border-[#6366F1]/20" data-id="element-541">
                  <div className="bg-[#1a1a2e] p-4 sm:p-6" data-id="element-542">
                    <div className="bg-[#0D0D0D] rounded-lg p-4 border border-gray-700" data-id="element-543">
                      <p className="text-[#F5F5F7]/60 text-sm mb-3" data-id="element-544">
                        Unknown sender: not added to Calendar yet
                      </p>
                      <p className="text-[#F5F5F7]/50 text-xs mb-4" data-id="element-545">
                        It looks like you've never been in contact with this
                        sender. This event hasn't been added to your calendar.
                        It won't appear there unless you say you know the
                        sender.
                      </p>
                      <div className="flex gap-3 mb-4" data-id="element-546">
                        <div className="relative" data-id="element-547">
                          <button className="px-4 py-2 bg-[#1a1a2e] border border-[#6366F1] rounded text-[#F5F5F7] text-sm" data-id="element-548">
                            I know the sender
                          </button>
                          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap" data-id="element-549">
                            <div className="bg-[#6366F1] text-white text-xs px-3 py-1.5 rounded-lg" data-id="element-550">
                              Press this "I know the sender" button
                            </div>
                          </div>
                        </div>
                        <button className="px-4 py-2 bg-transparent border border-gray-600 rounded text-[#F5F5F7]/60 text-sm" data-id="element-551">
                          Report spam
                        </button>
                      </div>
                      <p className="text-[#F5F5F7]/40 text-xs mt-8 text-center uppercase tracking-wide" data-id="element-552">
                        Otherwise the event you just scheduled with us will not
                        show up in your calendar and you will likely miss the
                        call
                      </p>
                    </div>
                  </div>
                </div>}
            </motion.div>)}
        </div>

        {/* Closing Message */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.8
      }} className="text-center mt-12 sm:mt-16" data-id="element-553">
          <h3 className="text-xl sm:text-2xl font-bold text-[#F5F5F7] mb-3" data-id="element-554">
            That's it!
          </h3>
          <p className="text-[#F5F5F7]/70 text-base sm:text-lg" data-id="element-555">
            We'll be in touch and talk to you at your booked time.
          </p>
        </motion.div>
      </main>

      {/* Footer */}
      <div className="max-w-6xl mx-auto px-4" data-id="element-556">
        <Footer data-id="element-557" />
      </div>
    </div>;
};